import { useEffect, useState } from "react";
import { ArrowRight, CalendarDays, CheckCircle2, Layers3, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, ApiError, setCsrfToken } from "./api";
import type { MeResponse } from "./types";
import { Dashboard } from "./components/Dashboard";
import { SetupWizard } from "./components/SetupWizard";

type Bootstrap = {
  setupRequired: boolean;
  product: string;
  tenant: { slug: string; name: string } | null;
};

function LoadingScreen() {
  return <main className="loading-screen" data-theme="midnight"><div className="loading-orbit"><div className="brand-mark large">M</div><span/><span/><span/></div><p>Aligning your calendars…</p></main>;
}

function UnavailableScreen() {
  return <main className="loading-screen" data-theme="midnight"><div className="brand-mark large">M</div><div className="bootstrap-error"><h1>Meridian could not start.</h1><p>The server bootstrap endpoint is unavailable. Check the Pterodactyl console, then try again.</p><Button onClick={() => window.location.reload()}>Retry connection</Button></div></main>;
}

function SignedOut({ bootstrap }: { bootstrap: Bootstrap }) {
  return <main className="signed-out-shell" data-theme="midnight"><section className="signed-out-panel"><div className="signed-out-art"><div className="brand-mark large">M</div><div className="orbit-line one"><span/></div><div className="orbit-line two"><span/></div><div className="orbit-line three"><span/></div></div><div className="signed-out-copy"><p className="eyebrow">Meridian Calendar</p><h1>Your studio’s time,<br/>finally in one orbit.</h1><p>Sign in to {bootstrap.tenant?.name ?? "your workspace"} to view personal, imported, and team calendars in one de-duplicated schedule.</p><Button size="lg" onClick={() => { window.location.href = `/auth/login?tenant=${encodeURIComponent(bootstrap.tenant?.slug ?? "")}&returnTo=/`; }}>Continue with SSO <ArrowRight/></Button><div className="trust-row"><span><ShieldCheck/>OIDC + PKCE</span><span><Layers3/>Smart de-duplication</span><span><CalendarDays/>Live iCal feeds</span></div></div></section><footer><CheckCircle2/>No separate Meridian password. Your workspace identity controls access.</footer></main>;
}

export default function App() {
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const boot = await api<Bootstrap>("/api/bootstrap");
        setBootstrap(boot);
        if (boot.setupRequired) { setLoading(false); return; }
        try {
          const current = await api<MeResponse>("/api/me");
          setCsrfToken(current.csrfToken);
          document.documentElement.dataset.theme = current.preferences.theme;
          setMe(current);
          setLoading(false);
        } catch (error) {
          if (!(error instanceof ApiError) || error.status !== 401) throw error;
          const deliberatelySignedOut = window.location.pathname === "/logged-out" || new URLSearchParams(window.location.search).has("signedOut");
          if (!deliberatelySignedOut && boot.tenant) {
            window.location.replace(`/auth/login?tenant=${encodeURIComponent(boot.tenant.slug)}&returnTo=/`);
            return;
          }
          setLoading(false);
        }
      } catch {
        setFailed(true);
        setLoading(false);
      }
    })();
  }, []);

  if (failed) return <UnavailableScreen/>;
  if (loading || !bootstrap) return <LoadingScreen/>;
  if (bootstrap.setupRequired) return <SetupWizard/>;
  if (!me) return <SignedOut bootstrap={bootstrap}/>;
  return <Dashboard initialMe={me}/>;
}
